<template>
  <div>
    <div class="mc-editable-table">
      <div :class="hasMargin ? 'clearfix mb-4' : 'clearfix mb-0'">
        <div class="d-flex mc-list-header">
          <h4 class="card-title flex-grow-1 d-flex align-items-center">
            {{ $t('report.title.determineRecipients') }}
          </h4>
          <McButton
            v-if="selectedIdsLength"
            variant="secondary-red"
            class="mx-2 ms-auto"
            icon="icon-outline_trash"
            :text="$t('global.delete')"
            @click="$refs['mc-modal-delete'].show()"
          />

          <McButton
            variant="primary"
            class="ms-auto"
            :text="$t('global.create')"
            icon="icon-fill_plus"
            :disabled="isAdding || isEditingRow != null"
            @click="handleAdd"
          />
        </div>
      </div>

      <div class="pb-4">
        <b-row>
          <slot name="description" />
          <slot name="invalid-feedback" />
          <b-col md="4" class="mc-row-check-wrapper">
            <h6 v-if="selectedIdsLength" class="mc-row-check mt-0 mb-2">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              {{ $t('global.object.selectedRow') }}
              <a class="color-primary" @click="handleUnselectAll">{{
                $t('global.action.deselect')
              }}</a>
            </h6>
            <McAlert
              v-if="invalidKeysLength && !selectedIdsLength"
              variant="error"
              text="Baris data error."
            >
              <template #prefix>
                <div class="mc-alert__custom_prefix">
                  <p>{{ invalidKeysLength }}</p>
                </div>
              </template>
            </McAlert>
          </b-col>
        </b-row>

        <FormulateForm
          ref="formRef"
          v-model="formValues"
          name="editable-table-form"
          :errors="formulateFormErrors"
          @submit="handleSubmit"
          @failed-validation="handleFailedValidation"
        >
          <div
            :class="`table-responsive border border-1 mc-loads-table__editable-table ${
              selectedIdsLength ? '' : 'mt-4'
            }`"
          >
            <b-table
              v-if="!isLoading"
              :items="items"
              :fields="computedFields"
              :sticky-header="`300px`"
              fixed
              show-empty
              class="editable-table mc-editable-table--multiselect mc-table-contact"
            >
              <!-- Empty Result -->
              <template #empty>
                <div class="text-center my-4">{{ $t('global.emptyTable') }}</div>
              </template>

              <!-- Slot: Header Checkbox -->
              <template #head(checkbox)>
                <div class="form-check" style="width: 10px">
                  <b-form-checkbox
                    v-model="selectedAll"
                    :disabled="disabledGlobal"
                    @change="handleSelectAll"
                  />
                </div>
              </template>

              <!-- Slot: Header Data -->
              <template #head()="data">
                <div class="d-flex justify-content-between" @click="handleSortChange(data)">
                  {{ data.label }}
                  <template v-if="data.field.isSortable">
                    <div>
                      <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                        <i class="mc-icon icon-outline_chevron-up"></i>
                      </div>
                      <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                        <i class="mc-icon icon-outline_chevron-down"></i>
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <!-- Slot: Cell Checkbox -->
              <template #cell(checkbox)="data">
                <div class="form-check">
                  <b-form-checkbox
                    v-model="selectedIds[data.item.id]"
                    :disabled="disabledGlobal"
                    @change="(event) => handleSelect(event, data)"
                  />
                </div>
              </template>

              <!-- Slot: All Cells -->
              <template #cell()="{ index, field, value, item }">
                <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
                  <FormulateInput
                    v-if="field.type === 'phone-number'"
                    :ref="`${field.key}-ref`"
                    v-bind="{ ...field }"
                    :rowItem="item"
                    :label="null"
                    :name="field.inputName || field.key"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting || field.disabled"
                    size="sm"
                    :validation-behavior="field.validationBehavior"
                    :error-behavior="field.errorBehavior"
                    @countryCode="getCountryCode"
                  />

                  <FormulateInput
                    v-else-if="field.type === 'select-resourceful-v2'"
                    v-bind="{ ...field }"
                    :fullObject="fullObjects[field.name]"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    :validation-behavior="field.validationBehavior"
                    :error-behavior="field.errorBehavior"
                    size="sm"
                    @update:fullObject="($event) => setFullObject(field.name, $event)"
                    @addTag="customTag"
                  />

                  <FormulateInput
                    v-else
                    v-bind="{ ...field }"
                    :fullObject="fullObjects[field.name]"
                    :rowItem="item"
                    :label="null"
                    :name="field.name"
                    :type="field.type || 'text'"
                    :disabled="isSubmitting"
                    :help="field?.validation?.includes('required')"
                    class="p-0"
                    :validation-behavior="field.validationBehavior"
                    :error-behavior="field.errorBehavior"
                    size="sm"
                    @update:fullObject="($event) => setFullObject(field.name, $event)"
                  />
                </template>

                <!-- Bold First Column -->
                <template v-else-if="field.isRedirectable">
                  <div class="fw-bold text-primary">
                    {{ value }}
                  </div>
                </template>

                <!-- Show Only Column -->
                <template v-else-if="!field.isEditable && isEditingRow == index">
                  <div>
                    {{ transformCellValue(value, field) }}
                  </div>
                </template>

                <template v-else>
                  <div
                    class="td__overflow-ellipsis"
                    :class="
                      invalidItems[item.id] && invalidItems[item.id][field.name]
                        ? 'td__invalid-data'
                        : undefined
                    "
                  >
                    {{ transformCellValue(value, field, item) }}
                  </div>
                </template>
              </template>

              <!-- Slot: Action Column (Last Column) -->
              <template #cell(action)="data">
                <div v-if="isEditingRow == data.index">
                  <b-button variant="outline" class="__accept-button btn" type="submit">
                    <i class="mc-icon icon-fill_checkmark"></i>
                  </b-button>

                  <b-button
                    variant="outline"
                    class="__cancel-button btn"
                    @click="handleEditCancel(data)"
                  >
                    <i class="mc-icon icon-outline_close"></i>
                  </b-button>
                </div>

                <b-button
                  v-else
                  variant="outline"
                  :class="{
                    '__edit-button btn': true,
                    __disabled: isEditingRow !== null,
                  }"
                  @click="handleEditStart(data)"
                >
                  <i class="mc-icon icon-outline_edit"></i>
                </b-button>
              </template>
            </b-table>
          </div>
        </FormulateForm>

        <!-- Temporary commented out -->
        <!-- <div class="d-flex flex-wrap flex-md-nowrap mt-4">
          <div class="mc-limit">
            <div class="__limit">
              <div>{{ $t('global.show') }}</div>
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="disabledGlobal"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              />
              <div>{{ $t('global.object.data') }}.</div>
            </div>

            <div v-if="totalCount === 0" class="__info">
              <strong>{{ totalCount }}</strong> {{ $t('global.object.from') }}
              <strong>{{ totalCount }}</strong> {{ $t('global.object.data') }}.
            </div>

            <div v-else class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.from') }} <strong>{{ totalCount }}</strong>
              {{ $t('global.object.data') }}.
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="disabledGlobal"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            />
          </div>
        </div> -->
      </div>
    </div>
    <b-modal
      id="mc-modal-delete"
      ref="mc-modal-delete"
      class="mc-modal"
      :title="$t('global.delete')"
      size="md"
      centered
      scrollable
    >
      <div class="card-body p-2">
        <p>
          {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
          {{ $t('global.confirmDelete2') }}
        </p>
      </div>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.cancel')"
            @click="cancel()"
          />
          <McButton variant="primary" :text="$t('global.delete')" @click="handleDelete" />
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import parsePhoneNumber from 'libphonenumber-js'
import { mapGetters } from 'vuex'

export default {
  components: {
    McButton,
    McAlert,
  },

  mixins: [Helper],
  props: {
    initialItems: {
      type: Array,
      default: () => [],
    },
    limitOptions: {
      default: () => [5, 10, 25, 50, 100],
    },
    // invalidFields: { type: Array },
    fields: {
      type: Array,
    },
    hasData: {
      type: Boolean,
    },
    hasMargin: {
      type: Boolean,
      default: true,
    },
    dataStore: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isSubmitting: false,
      limit: this.limitOptions[0] ?? 10,
      page: 1,
      totalCount: 0,
      ids: [],
      items: this.initialItems ?? [],
      sorts: [],
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      isEditingRow: null,
      search: '',
      formValues: {},
      isAdding: null,
      loadsData: {},
      showResponse: {},
      invalidItems: {},
      formulateFormErrors: {},
      fullObjects: {
        user: {},
      },
      render: true,
      isEmpty: !this.hasData,
      initialCountryCode: '62',
      countryCode: '62',
      isLoading: false,
      data: [],
      listDatas: [],
    }
  },

  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    disabledGlobal() {
      return this.isEditingRow !== null || this.isBusy
    },
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },
    computedFields() {
      return [
        {
          key: 'checkbox',
          label: '',
          thStyle: 'width: 40px',
        },
        ...this.fields,
        {
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        },
      ]
    },
    invalidKeysLength() {
      return Object.keys(this.invalidItems).length
    },
    hasWhatsapp() {
      return this.hasAccessAll(['whatsapp-integration:read'])
    },
  },
  watch: {
    async formValues(newValue, oldValue) {
      if (this.isEditingRow == null && !this.invalidKeysLength) return

      // get the field that is currently changing
      const [patchData, hasChanged] = this.filterUnchangedData(newValue, this.loadsData)

      if (!hasChanged) return

      const changedFields = Object.keys(patchData)
      changedFields.forEach((field) => {
        if (!this.formulateFormErrors[field]) return

        // delete the field in formulateFormErrors
        this.$delete(this.formulateFormErrors, field)
      })
    },
    countryCode: function (value) {
      if (!value) {
        this.countryCode = this.initialCountryCode
      }
    },
    items(value) {
      this.$emit('updateData', value)
    },
  },

  async mounted() {
    this.fields.forEach((el) => {
      if (el.isSortable) el._sort = 'desc'
      if (el.orderBy) this.sorts.push({ [el.key]: el.orderBy })
    })
  },

  methods: {
    loadInitialItems(value) {
      this.items = value.map((item, index) => {
        return {
          user: item,
          ...item,
          contactId: item.contactId ? item.contactId : undefined,
        }
      })
    },
    trimPhoneNo(value) {
      if (!value) return

      let noTelp = value
      if (value[0] == '0') noTelp = this.initialCountryCode + value.slice(1, value.length)

      if (!this.countryCode) this.countryCode = this.initialCountryCode
      noTelp = ('+' + value).includes('+' + this.countryCode) ? value : this.countryCode + value

      let phoneNumber = parsePhoneNumber(`+${noTelp.toString()}`)

      const countryCode = phoneNumber?.countryCallingCode
      this.countryCode = countryCode

      this.$emit('updateCountryCode', this.countryCode)

      return phoneNumber?.nationalNumber
    },

    stringSorts() {
      return this.sorts.reduce((next, ob) => {
        const str = JSON.stringify(ob)
        return [...next, str]
      }, [])
    },
    handleFailedValidation() {
      return this.$mcToast.error('Terdapat kesalahan format pada input anda')
    },
    transformCellValue(value, field, item) {
      if (value === false) return '-'

      if (field.name === 'phoneNumber' && value) {
        const phoneNumber = parsePhoneNumber(`+${value.toString()}`)
        return phoneNumber?.number
      }

      if (field.name === 'user') {
        if (value) return value
        else return item.label
      }
      if (typeof value === 'number') return value.toLocaleString('id-ID')
      return value
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)
      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            if (!formValues[key]) return { [key]: null }
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },

    async handleLimitChange(_limit) {
      this.limit = _limit
      this.page = 1
    },

    async handlePageChange(newPage) {
      this.page = newPage
    },

    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },

    async handleSortChange(data) {
      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return
      if (this.isEditingRow !== null) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      this.handleSort()
    },
    handleSort() {
      if (this.sorts.length > 0) {
        for (let i = 0; i < this.sorts.length; i++) {
          this.items = this.items.sort((a, b) => {
            const aValue = a[this.sorts[i].key]
            const bValue = b[this.sorts[i].key]
            if (aValue < bValue) return -1
            if (aValue > bValue) return 1
            return 0
          })
        }
      }
    },
    async handleEditStart(data) {
      if (this.selectedIdsLength) this.handleUnselectAll()
      if (this.isEditingRow != null) return
      const loadData = this.items.find((item) => item.id == data.item.id)

      this.items = this.items.map((item) => {
        if (item.id == data.item.id) return loadData
        return item
      })

      // assign formValues with response show data
      this.fields.forEach((field) => {
        if (!field.isEditable) return
        let value
        if (field.name === 'phoneNumber') value = this.trimPhoneNo(loadData.phoneNumber)
        else if (field.name === 'user')
          value = loadData.user && !loadData.user.id ? 9999999 : loadData.user.id
        else value = loadData[field.name]

        this.fullObjects.user = {
          email: loadData.email ?? null,
          phoneNumber: loadData.phoneNumber ?? null,
          userId: loadData.user ? (loadData.user.userId ? loadData.user.userId : 9999999) : 9999999,
          id: loadData.user ? (loadData.user.id ? loadData.user.id : 9999999) : 9999999,
          label: loadData.user ? loadData.user.label : null,
        }

        this.$set(this.formValues, field.name, value)
      })

      if (data.item.contactId) this.$set(this.formValues, 'contactId', data.item.contactId)
      if (data.item.id) this.$set(this.formValues, 'id', data.item.id)

      this.isEditingRow = data.index
      this.showResponse = { ...loadData }

      this.loadsData = this.formValues

      // assign the form errors from invalidItems with the current id
      if (this.invalidItems[loadData.id]) {
        this.formulateFormErrors = { ...this.invalidItems[loadData.id] }
      }

      await this.$nextTick()
    },
    handleEditCancel(data) {
      if (this.isSubmitting) return
      if (this.isAdding) {
        const [, ...newItems] = this.items
        this.items = newItems
        this.isAdding = false
      } else {
        // assign showResponse to items if cancel edit
        const newItems = [...this.items]
        newItems.splice(data.index, 1, this.showResponse)
        this.items = newItems
      }

      this.formulateFormErrors = {}

      this.isEditingRow = null
    },
    submitStore(data) {
      const tag = {
        id: data.id ?? this.items.length + 1,
        user: { ...this.fullObjects.user },
        contactId: data.contactId ?? undefined,
        phoneNumber: data.phoneNumber ? this.countryCode + data.phoneNumber : null,
        email: data.email,
      }

      if (!tag.phoneNumber && !tag.email) {
        this.$mcToast.error(this.$t('report.text.contactValidation'))
        return false
      }
      this.items = [tag, ...this.items.filter((item) => item.user.userId != 0)]
      this.totalCount = this.items.length

      // Clear form state
      this.formValues = {}
      this.fullObjects = { user: {} }
      this.countryCode = this.initialCountryCode
      return true
    },
    submitUpdate(data) {
      if (this.isEditingRow === null) return true
      const tag = {
        id: data.id ?? this.items.length + 1,
        user: { ...this.fullObjects.user },
        contactId: data.contactId ?? undefined,
        phoneNumber: data.phoneNumber ? this.countryCode + data.phoneNumber : null,
        email: data.email,
      }

      if (!tag.phoneNumber && !tag.email) {
        this.$mcToast.error(this.$t('report.text.contactValidation'))
        return false
      }

      this.items = this.items.map((item, index) => {
        if (index == this.isEditingRow) return tag
        return item
      })
      this.totalCount = this.items.length

      // Clear form state
      this.formValues = {}
      this.fullObjects = { user: {} }
      this.countryCode = this.initialCountryCode
      return true
    },
    async handleSubmit(data) {
      this.isLoading = true
      if (this.isSubmitting) {
        this.isLoading = false
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      const transformedData = this.transformFields(data, this.fields)
      let filteredItems = this.items.filter(
        (item, index) => this.isEditingRow !== null && index !== this.isEditingRow
      )
      if (
        filteredItems
          .filter((item) => item.id !== undefined && item.phoneNumber !== null)
          .some(
            (item) =>
              item.phoneNumber === this.countryCode + this.trimPhoneNo(transformedData.phoneNumber)
          )
      ) {
        this.isLoading = false
        this.$mcToast.error(this.$t('report.text.samePhoneWA'))
        return
      }

      if (
        filteredItems
          .filter(
            (item) => item.id !== undefined && item.email !== null && item.email.trim().length > 1
          )
          .some((item) => item.email === transformedData.email)
      ) {
        this.isLoading = false
        this.$mcToast.error(this.$t('report.text.sameEmail'))
        return
      }

      let res = false
      if (this.isAdding) {
        res = this.submitStore(transformedData)
      } else {
        res = this.submitUpdate(transformedData)
      }
      if (!res) {
        this.isLoading = false
        return
      }

      this.isSubmitting = true
      this.isEditingRow = null
      this.isAdding = false
      this.isSubmitting = false
      this.isLoading = false
      this.handleSort()
    },
    filterUnchangedData(newData, oldData) {
      const keys = new Set([...Object.keys(newData)])
      const _newValue = {}
      keys.forEach((key) => {
        if (newData[key] == oldData[key]) return
        _newValue[key] = newData[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    async handleDelete() {
      const itemsToDelete = Object.keys(this.selectedIds).filter((key) => this.selectedIds[key])

      // Update local table data
      this.items = this.items.filter((item) => !itemsToDelete.includes(item.id.toString()))
      this.totalCount = this.items.length

      // Reset selection state
      this.handleUnselectAll()
      this.$refs['mc-modal-delete'].hide()
    },
    handleSelect(checked, data) {
      this.selectedIds = { ...this.selectedIds, [data.item.id]: checked }
      if (checked) {
        this.selectedIdsLength += 1
        if (this.selectedIdsLength == this.totalCount) this.selectedAll = true
      } else {
        this.selectedAll = false
        this.selectedIdsLength -= 1
      }
    },
    handleSelectAll(event) {
      if (event) {
        this.selectedIds = Object.fromEntries(
          this.items.map((data) => [typeof data === 'number' ? data : data.id, true])
        )
        this.selectedIdsLength = this.items.length
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },
    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },
    handleAdd() {
      if (this.isEditingRow != null) return
      if (this.items.length >= 200) {
        this.$mcToast.error(this.$t('report.text.maximumRecipients'))
        return
      }
      this.isEditingRow = 0
      this.isAdding = true

      this.isEmpty = false
      this.items = [
        {
          email: null,
          phoneNumber: null,
          user: {
            label: null,
            id: 0,
            userId: 0,
            email: null,
            phoneNumber: null,
          },
        },
        ...this.items,
      ]
    },

    customTag(value) {
      const tag = {
        label: value,
        id: 9999999,
        userId: 9999999,
        email: this.formValues.email,
        phoneNumber: this.formValues.phoneNumber,
      }

      this.$emit('customTag', tag)
      this.setFullObject('user', tag)
      this.$set(this.formValues, 'user', tag.id)
      this.$set(this.formValues, 'phoneNumber', tag.phoneNumber ?? null)
      this.$set(this.formValues, 'email', tag.email ?? null)
      this.$set(this.formValues, 'id', this.items.length + 1)
    },

    setFullObject(field, value) {
      if (field === 'user') {
        const phoneNumber = value?.phoneNumber ?? value?.phoneNo ?? null
        this.$set(
          this.formValues,
          'phoneNumber',
          phoneNumber ? this.trimPhoneNo(phoneNumber) : null
        )
        this.$set(this.formValues, 'email', value.email ?? null)
      }
      this.fullObjects[field] = value
    },

    getCountryCode(value) {
      this.countryCode = value
      this.$emit('updateCountryCode', value)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
